'use client';

import { Composite } from '@floating-ui/react';
import { forwardRef, useCallback, useMemo, useState } from 'react';

import { css, cx } from '@/styled-system/css';

import { useChildrenOfType } from '../../hooks/use-children-of-type';
import { useControllableState } from '../../hooks/use-controllable-state';
import { Slot } from '../slot';
import { AccordionRootContext } from './context/accordion-root.context';
import { accordionRootStyles } from './styles/accordion-root.styles';
import type { AccordionRootPropsInterface } from './types/accordion-root.types';

/**
 * @subcomponents ['Accordion.Item', 'Accordion.Header', 'Accordion.Panel']
 * @since 8.43.0
 */

export const AccordionRoot = forwardRef<HTMLUListElement, AccordionRootPropsInterface>(
  (props: AccordionRootPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      children,
      className,
      css: cssProp,
      defaultExpanded,
      expanded,
      onExpandedChange,
      onPanelFocus,
      type = 'multiple',
      shouldAwaitInteractionResponse = true,
      ...rest
    } = props;

    const [expandedState, setExpandedState] = useControllableState({
      prop: expanded,
      defaultProp: defaultExpanded,
      onChange: onExpandedChange,
    });

    const toggleExpanded = useCallback(
      (value: string) => {
        setExpandedState((prevState) => {
          if (!prevState) return value;

          if (type === 'single') {
            return value === prevState ? '' : value;
          }

          if (typeof prevState === 'string') {
            return prevState === value ? [] : [prevState, value];
          }

          if (Array.isArray(prevState)) {
            if (prevState.includes(value)) {
              return prevState.filter((item) => item !== value);
            }
            return [...prevState, value];
          }
        });
      },
      [setExpandedState, type],
    );

    const accordionItems = useChildrenOfType(children, 'Accordion.Item');

    const initialAccordionItemIndex = useMemo(() => {
      return expandedState
        ? accordionItems.findIndex((accordionItem) => accordionItem.props.value === expandedState)
        : 0;
    }, [accordionItems, expandedState]);

    const [activeIndex, setActiveIndex] = useState(initialAccordionItemIndex);

    const onFocusChange = useCallback(
      (index: number) => {
        setActiveIndex(index);
        if (accordionItems?.[index]?.props.value) {
          onPanelFocus?.(accordionItems?.[index]?.props.value);
        }
      },
      [accordionItems, onPanelFocus],
    );

    const Component = asChild ? Slot : 'ul';

    const styles = css(accordionRootStyles, cssProp);

    return (
      <AccordionRootContext.Provider
        value={{
          expanded: expandedState,
          toggleExpanded,
          onExpandedChange,
          onPanelFocus,
          shouldAwaitInteractionResponse,
        }}
      >
        {/* Intentionally not allowing user overrides for Composite */}
        <Composite loop activeIndex={activeIndex} onNavigate={onFocusChange}>
          <Component
            className={cx('c11n', styles, className)}
            data-c11n-component="Accordion.Root"
            data-c11n-version={__C11N_VERSION__}
            ref={forwardedRef}
            {...rest}
          >
            {children}
          </Component>
        </Composite>
      </AccordionRootContext.Provider>
    );
  },
);

AccordionRoot.displayName = 'Accordion.Root';
